import React, { useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import LoadingSpinner from './LoadingSpinner'
import CompanyLogo from './CompanyLogo'
import MFAVerification from './MFAVerification'
import RecaptchaNotice from './RecaptchaNotice'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [mfaResolver, setMfaResolver] = useState(null)
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  async function handleSubmit(e) {
    e.preventDefault()

    if (!email || !password) {
      return setError('Please fill in all fields')
    }

    try {
      setError('')
      setLoading(true)
      await login(email, password)
      
      // Check if there's a return URL from the redirect
      const returnUrl = location.state?.returnUrl || '/dashboard'
      navigate(returnUrl, { replace: true })
    } catch (error) {
      console.error('Login error:', error)
      
      // Handle MFA requirement
      if (error.code === 'auth/multi-factor-auth-required') {
        setMfaResolver(error.resolver)
        setLoading(false)
        return
      }
      
      // Handle specific Firebase auth errors
      switch (error.code) {
        case 'auth/user-not-found':
          setError('No account found with this email address')
          break
        case 'auth/wrong-password':
          setError('Incorrect password')
          break
        case 'auth/invalid-email':
          setError('Invalid email address')
          break
        case 'auth/user-disabled':
          setError('This account has been disabled')
          break
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later')
          break
        default:
          setError('Failed to log in. Please check your credentials')
      }
    }
    setLoading(false)
  }

  const handleMFASuccess = () => {
    setMfaResolver(null)
    // Check if there's a return URL from the redirect
    const returnUrl = location.state?.returnUrl || '/dashboard'
    navigate(returnUrl, { replace: true })
  }

  const handleMFACancel = () => {
    setMfaResolver(null)
    setError('Multi-factor authentication cancelled')
  }

  return (
    <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-4">
            <div className="card shadow-lg border-0">
              <div className="card-body p-5">
                <div className="text-center mb-4">
                  <CompanyLogo size="md" className="mb-4" />
                  <p className="text-muted">Sign in to your account</p>
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="email" className="form-label">Email Address</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-envelope"></i>
                      </span>
                      <input
                        type="email"
                        id="email"
                        className="form-control"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">Password</label>
                    <div className="input-group">
                      <span className="input-group-text">
                        <i className="fas fa-lock"></i>
                      </span>
                      <input
                        type="password"
                        id="password"
                        className="form-control"
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="d-grid gap-2">
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg"
                      disabled={loading}
                    >
                      {loading ? <LoadingSpinner size="sm" /> : 'Sign In'}
                    </button>
                  </div>
                </form>

                <div className="text-center mt-4">
                  <Link to="/forgot-password" className="text-decoration-none">
                    Forgot your password?
                  </Link>
                </div>

                <hr className="my-4" />

                <div className="text-center">
                  <p className="mb-0">
                    Need an account?{' '}
                    <Link to="/register" className="text-decoration-none fw-bold">
                      Sign up
                    </Link>
                  </p>
                </div>

                <RecaptchaNotice />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MFA Verification Modal */}
      {mfaResolver && (
        <MFAVerification
          mfaResolver={mfaResolver}
          onSuccess={handleMFASuccess}
          onCancel={handleMFACancel}
        />
      )}
    </div>
  )
}
